import { filterConstants } from "../../constants";
import {
  useApiPagedMetaSync,
  useGetPageQueryParams,
  useMerchantApiQueryWithParams,
  useQueryString,
  useReactRouterLink,
} from "../../hooks";
import {
  IQueryStringNavLinks,
  ITableHeader,
  IViewTopUp,
  IPageAndSearchRequest,
  PagedMeta,
} from "../../interfaces-and-types";
import { View } from "./View";

interface ITopUpQueryMeta {
  status: string;
}

export interface ITopUpViewProps {
  data: IViewTopUp[];
  headers: ITableHeader[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  queryStringTabs: IQueryStringNavLinks;

  // modals
  openCreateNewModal: () => void;
  openDetailsModal: (data: any) => void;
  refreshTable: () => void;
  navigateToViewBalance: () => void;

  // extra buttons
  onExport: () => void;
}

export const TopUps = () => {
  const query = useQueryString();
  const status = query.get(filterConstants.statusKey);
  const { page, page_size, search } = useGetPageQueryParams();

  const { navigate, navigateToRoutePreserveParams } = useReactRouterLink();

  const {
    isLoading: isLoadingTopUps,
    isError: isErrorTopUps,
    data: topUps,
    meta: topUpsMeta,
    refetch: refetchTopUps,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & ITopUpQueryMeta,
    IViewTopUp[],
    PagedMeta
  >([
    "getTopUps",
    {
      page: (page && Number(page)) || 1,
      page_size: (page_size && Number(page_size)) || 10,
      search,
      status: status || "successful",
    },
  ]);

  useApiPagedMetaSync(topUpsMeta?.page_meta);

  const headers: ITableHeader[] = [
    {
      key: "amount",
      header: "Amount",
    },
    {
      key: "charged_amount",
      header: "Paid",
    },
    {
      key: "currency",
      header: "Currency",
    },
    {
      key: "status",
      header: "Status",
    },
    // {
    //   key: "description",
    //   header: "Description",
    // },
    // {
    //   key: "gateway",
    //   header: "Provider",
    // },
    {
      key: "payment_type",
      header: "Source",
    },
    {
      key: "bank_trsf_account_name",
      header: (
        <>
          Account Name /
          <br />
          Merchant Name
        </>
      ),
    },
    {
      key: "bank_trsf_account_number",
      header: (
        <>
          Account No/
          <br />
          Merchant Code
        </>
      ),
    },
    {
      key: "bank_trsf_session_id",
      header: "Session Id",
    },
    {
      key: "date",
      header: "Date",
    },
    {
      key: "more",
      header: "",
    },
  ];

  const queryStringTabs: IQueryStringNavLinks = {
    query: filterConstants.statusKey,
    options: [
      {
        key: "Successful",
        value: "successful",
        description: (
          <>
            Congrats! SwitchApp has{" "}
            <span className="text-success">
              received and processed the funds
            </span>
            . Check your balance.
          </>
        ),
      },
      {
        key: "Pending",
        value: "pending",
        description: (
          <>
            SwitchApp has{" "}
            <span className="text-success">received the funds</span>. We are
            currently working to get it to your balance.
          </>
        ),
      },
      {
        key: "Processing",
        value: "processing",
        description: (
          <>
            SwitchApp{" "}
            <span className="text-warning">
              is waiting to received the funds
            </span>
            . Network delays may be to blame. Please wait a little while, then
            reach out to us.
          </>
        ),
      },
      {
        key: "Failed",
        value: "failed",
        description:
          "SwitchApp never received the funds from this top-up attempt.",
      },
    ],
  };

  const onExport = () => {
    alert("exporting data...");
  };

  const refreshTable = () => refetchTopUps();

  const openDetailsModal = (id: any) =>
    navigateToRoutePreserveParams(`${id}/details`);

  const openCreateNewModal = () => {
    navigateToRoutePreserveParams(`create`);
  };

  const navigateToViewBalance = () => navigate("/balances");

  return (
    <View
      data={topUps || []}
      headers={headers}
      isLoading={isLoadingTopUps}
      isError={isErrorTopUps}
      totalCount={topUpsMeta?.page_meta.item_count || 0}
      queryStringTabs={queryStringTabs}
      onExport={onExport}
      openCreateNewModal={openCreateNewModal}
      openDetailsModal={openDetailsModal}
      navigateToViewBalance={navigateToViewBalance}
      refreshTable={refreshTable}
    />
  );
};
